import * as React from 'react';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { OnboardingContainer } from '../../../components/displays/OnboardingContainer';
import { OnboardingCard } from '../../../components/displays/OnboardingCard';
import { Box, Button, CircularProgress, FormControl, FormLabel, Stack } from '@mui/material';
import LanguageSelector from '../../../components/inputs/LanguageSelector';
import PasswordField from '../../../components/inputs/PasswordField/PasswordField';

export default function ResetPassword(props: { onReset?: (password: string) => Promise<void>; onSignIn?: () => void }) {
    const { t, i18n } = useTranslation();

    const [newPassword, setNewPassword] = React.useState('');
    const [newPasswordError, setNewPasswordError] = React.useState(false);
    const [newPasswordErrorMessage, setNewPasswordErrorMessage] = React.useState('');

    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [confirmPasswordError, setConfirmPasswordError] = React.useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = React.useState('');

    const [resettingPassword, setResettingPassword] = React.useState(false);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (newPasswordError || confirmPasswordError) {
            return;
        }

        const data = new FormData(event.currentTarget);
        const password = data.get('newPassword') as string;

        if (props.onReset) {
            setResettingPassword(true);
            await props.onReset(password).finally(() => {
                setResettingPassword(false);
            });
        }
    };

    const validateInputs = () => {
        const newPassword = document.getElementById('newPassword') as HTMLInputElement;
        const confirmPassword = document.getElementById('confirmPassword') as HTMLInputElement;

        let isValid = true;

        if (!newPassword.value || newPassword.value.length < 8) {
            setNewPasswordError(true);
            setNewPasswordErrorMessage(t('common.form.invalidPassword'));
            isValid = false;
        } else {
            setNewPasswordError(false);
            setNewPasswordErrorMessage('');
        }

        if (newPassword.value !== confirmPassword.value) {
            setConfirmPasswordError(true);
            setConfirmPasswordErrorMessage(t('common.form.passwordMismatch'));
            isValid = false;
        } else {
            setConfirmPasswordError(false);
            setConfirmPasswordErrorMessage('');
        }

        return isValid;
    };

    return (
        <OnboardingContainer direction='column' justifyContent='space-between'>
            <OnboardingCard variant='outlined' style={{ position: 'relative' }}>
                <Stack direction='row' sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography component='h1' variant='h4' sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}>
                        {t('onboarding.resetPassword.title')}
                    </Typography>
                    <LanguageSelector onChange={(locale) => i18n.changeLanguage(locale)} />
                </Stack>
                <Box
                    component='form'
                    onSubmit={handleSubmit}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        gap: 2,
                    }}
                >
                    <FormControl>
                        <FormLabel htmlFor='newPassword'>{t('onboarding.resetPassword.newPassword')}</FormLabel>
                        <PasswordField
                            required
                            fullWidth
                            name='newPassword'
                            label={undefined}
                            id='newPassword'
                            error={newPasswordError}
                            helperText={newPasswordErrorMessage}
                            color={newPasswordError ? 'error' : 'primary'}
                            value={newPassword}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setNewPassword(event.target.value);
                            }}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor='confirmPassword'>{t('onboarding.resetPassword.confirmPassword')}</FormLabel>
                        <PasswordField
                            required
                            fullWidth
                            name='confirmPassword'
                            label={undefined}
                            id='confirmPassword'
                            error={confirmPasswordError}
                            helperText={confirmPasswordErrorMessage}
                            color={confirmPasswordError ? 'error' : 'primary'}
                            value={confirmPassword}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setConfirmPassword(event.target.value);
                            }}
                        />
                    </FormControl>
                    <Button type='submit' fullWidth variant='contained' onClick={validateInputs} sx={{ marginTop: 2 }} loading={resettingPassword} loadingIndicator={<CircularProgress size={16} />}>
                        {t('onboarding.resetPassword.title')}
                    </Button>
                </Box>
                <Typography sx={{ textAlign: 'center' }}>
                    <Button fullWidth onClick={props.onSignIn}>
                        {t('common.signIn')}
                    </Button>
                </Typography>
            </OnboardingCard>
        </OnboardingContainer>
    );
}
